import React from "react";
import {
  Watch,
  X as XIcon,
  Clock,
  Wrench,
  Gauge,
  Footprints,
  Camera,
  Sparkles,
  Coins,
  Wallet,
  Gift,
  HandHeart,
  Mic,
  Compass,
} from "lucide-react";

/**
 * TimeWatchGuide
 *
 * Popup informativo dedicato esclusivamente alla comprensione e all'utilizzo
 * del TIME Watch. Fa parte del Centro Guida di TIME Inside e si posiziona
 * fra il "Libretto di Benvenuto" (introduzione all'ecosistema) e "Comandi
 * Vocali" (interazione con Ora TIME).
 *
 * Filosofia:
 *  - NON insegna solo dove cliccare: spiega perché il TIME Watch esiste e
 *    quale nuovo modo di osservare il Tempo propone.
 *  - Additivo: non tocca né duplica alcuna logica del TIME Watch.
 *  - Design coerente con `VoiceCommandsGuide` e `TimeWatchWelcomeDialog`
 *    (backdrop scuro con blur, glass-card bordata emerald, tipografia
 *    "Apple-like", parola "Tempo" sempre capitalizzata).
 */

const MODES = [
  {
    icon: Clock,
    label: "OROLOGIO",
    text: "Per visualizzare l'ora insieme allo scorrere del Tempo.",
  },
  {
    icon: Wrench,
    label: "UTILITY",
    text: "Per utilizzare gli strumenti quotidiani e conservare tracce significative del proprio Tempo.",
  },
  {
    icon: Gauge,
    label: "TIME WATCH",
    text: "La modalità principale, nella quale il proprio rapporto con il Tempo viene rappresentato attraverso sette indicatori.",
  },
];

const INDICATORS = [
  {
    n: 1,
    icon: Sparkles,
    title: "Tempo per lasciare il Segno",
    text:
      "Rappresenta il Tempo che rimane prendendo come riferimento una vita ipotetica di 100 anni. Non è una previsione della durata della tua vita: è un riferimento visivo. Serve a trasformare qualcosa che normalmente percepiamo come indefinito — il Tempo che abbiamo davanti — in qualcosa che possiamo osservare.",
  },
  {
    n: 2,
    icon: Clock,
    title: "Tempo a disposizione oggi",
    text:
      "Mostra quanto Tempo rimane nella giornata fino alle 20:00. Ti permette di vedere immediatamente quanta parte del Tempo operativo della giornata hai ancora davanti.",
  },
  {
    n: 3,
    icon: Coins,
    title: "Denaro",
    text:
      "Qui inserisci il denaro che hai realmente a disposizione. Il valore economico viene mostrato inizialmente per quello che è: denaro. Nella schermata successiva accade però qualcosa di fondamentale.",
  },
  {
    n: 4,
    icon: Gauge,
    title: "Tempo Libero",
    text:
      "TIME applica la propria regola madre: 1 € = 1 minuto. Lo stesso denaro che hai appena osservato economicamente viene quindi trasformato in Tempo. TIME lo definisce Tempo Libero. Non stai più guardando soltanto quanti euro possiedi: stai osservando, secondo la filosofia TIME, quanto Tempo rappresentano quelle risorse.",
  },
  {
    n: 5,
    icon: Wallet,
    title: "Banconote Universali MITEL",
    text:
      "Il concetto viene portato anche nel mondo fisico attraverso le MITEL. Sono banconote universali del Tempo già realizzate e stampate, nelle quali il valore non viene rappresentato semplicemente attraverso il denaro, ma attraverso minuti di Tempo.",
  },
  {
    n: 6,
    icon: Gift,
    title: "Tempo Dato",
    text:
      "Rappresenta il Tempo che hai deciso di dedicare o donare agli altri. Perché una parte del nostro Tempo non viene utilizzata per noi stessi: viene data.",
  },
  {
    n: 7,
    icon: HandHeart,
    title: "Tempo Avuto",
    text:
      "Rappresenta invece il Tempo che altre persone hanno dedicato a te. TIME rende quindi osservabile anche uno degli scambi più comuni e allo stesso tempo meno contabilizzati della nostra vita: il Tempo che diamo e il Tempo che riceviamo.",
  },
];

const START = [
  "Apri il TIME Watch.",
  "Osserva i sette indicatori.",
  "Usa Impronte.",
  "Scegli i tuoi due Istanti.",
  "Guarda il tuo denaro trasformarsi in Tempo.",
  "Osserva quanto Tempo hai dato e quanto ne hai ricevuto.",
];


function SectionTitle({ eyebrow, title }) {
  return (
    <div className="mb-3">
      {eyebrow && (
        <div className="text-[10px] tracking-[0.32em] uppercase text-emerald-300/80 mb-1.5">
          {eyebrow}
        </div>
      )}
      <h3 className="text-lg sm:text-xl font-light text-white leading-snug tracking-tight">
        {title}
      </h3>
    </div>
  );
}

export default function TimeWatchGuide({ open, onClose, onBack }) {
  if (!open) return null;

  return (
    <div
      data-testid="time-watch-guide"
      className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="glass-card relative w-full max-w-2xl p-6 sm:p-8 border-emerald-400/25 shadow-[0_25px_80px_-20px_rgba(16,185,129,0.35)] max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="time-watch-guide-close"
          aria-label="Chiudi Guida TIME Watch"
          className="absolute top-4 right-4 p-1 rounded-full text-white/50 hover:text-white hover:bg-white/5 transition"
        >
          <XIcon className="w-4 h-4" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <div className="w-9 h-9 rounded-full bg-emerald-400/10 border border-emerald-400/25 flex items-center justify-center text-emerald-300">
            <Watch className="w-4 h-4" />
          </div>
          <div className="text-[10px] tracking-[0.32em] uppercase text-emerald-300">
            TIME Watch
          </div>
        </div>
        <h2
          data-testid="time-watch-guide-title"
          className="text-2xl sm:text-3xl font-light text-white tracking-tight leading-tight mb-2"
        >
          Guida Essenziale
        </h2>
        <p className="text-[13px] sm:text-sm font-light text-white/60 leading-relaxed mb-2">
          Non serve a gestire il Tempo. Serve a renderlo visibile.
        </p>

        {/* Divider */}
        <div className="h-px bg-white/8 my-6" />

        {/* 1. Prima di iniziare */}
        <section
          data-testid="tw-guide-section-intro"
          className="mb-7"
        >
          <SectionTitle eyebrow="1 · Prima di iniziare" title="Perché esiste" />
          <div className="space-y-3 text-[13px] sm:text-sm font-light text-white/70 leading-relaxed">
            <p>
              Il TIME Watch nasce da un principio semplice:{" "}
              <span className="text-white/90">
                non possiamo comprendere davvero il nostro rapporto con il Tempo
                finché non riusciamo a vederlo.
              </span>
            </p>
            <p>
              Per questo TIME Watch non vuole dirti cosa devi fare, come devi
              organizzare la giornata o quanto devi essere produttivo. Ti mette
              davanti alcuni indicatori e strumenti attraverso i quali puoi
              iniziare a osservare il tuo Tempo da prospettive differenti.
            </p>
            <p className="text-white/55">
              Attualmente il TIME Watch è un{" "}
              <span className="text-emerald-300/90">PMV — Prodotto Minimo Viabile</span>{" "}
              utilizzabile da smartphone, tablet e computer. È già stato inoltre
              realizzato un prototipo semifunzionante del futuro dispositivo da
              polso.
            </p>
          </div>
        </section>

        {/* 2. Le tre modalità */}
        <section
          data-testid="tw-guide-section-modes"
          className="mb-7"
        >
          <SectionTitle eyebrow="2 · Le tre modalità" title="Come è organizzato" />
          <ul className="space-y-2.5">
            {MODES.map((m) => {
              const Ico = m.icon;
              return (
                <li
                  key={m.label}
                  className="flex items-start gap-3 px-3.5 py-3 rounded-xl bg-white/[0.02] border border-white/10"
                >
                  <div className="w-8 h-8 rounded-lg bg-emerald-400/10 border border-emerald-400/20 flex items-center justify-center text-emerald-300 shrink-0">
                    <Ico className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[11px] tracking-[0.28em] uppercase text-emerald-300/90 mb-0.5">
                      {m.label}
                    </div>
                    <div className="text-[13px] sm:text-sm font-light text-white/75 leading-relaxed">
                      {m.text}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>

        {/* 3. Modalità Orologio */}
        <section
          data-testid="tw-guide-section-orologio"
          className="mb-7"
        >
          <SectionTitle eyebrow="3 · Modalità Orologio" title="L'ora e lo scorrere" />
          <div className="space-y-2.5 text-[13px] sm:text-sm font-light text-white/70 leading-relaxed">
            <p>
              Puoi utilizzare TIME Watch anche semplicemente come un orologio.
              Sotto l'orario è però presente un timer sincronizzato.
            </p>
            <p className="text-white/90">
              L'orologio ti dice che ore sono. Il timer ti ricorda che, mentre
              le guardi, il Tempo continua a scorrere.
            </p>
          </div>
        </section>

        {/* 4. Modalità Utility */}
        <section
          data-testid="tw-guide-section-utility"
          className="mb-7"
        >
          <SectionTitle eyebrow="4 · Modalità Utility" title="Strumenti quotidiani + tracce significative" />
          <p className="text-[13px] sm:text-sm font-light text-white/70 leading-relaxed mb-3">
            All'interno trovi quattro strumenti classici:{" "}
            <span className="text-white/90">Timer</span>,{" "}
            <span className="text-white/90">Promemoria</span>,{" "}
            <span className="text-white/90">Cronometro</span>,{" "}
            <span className="text-white/90">Sveglia</span>. A questi si
            aggiungono due strumenti caratteristici di TIME.
          </p>
          <div className="space-y-2.5">
            <div className="flex items-start gap-3 px-3.5 py-3 rounded-xl bg-white/[0.02] border border-white/10">
              <div className="w-8 h-8 rounded-lg bg-emerald-400/10 border border-emerald-400/20 flex items-center justify-center text-emerald-300 shrink-0">
                <Footprints className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <div className="text-[13px] sm:text-sm text-white/90 font-light mb-1">
                  Impronte
                </div>
                <p className="text-[12px] sm:text-[13px] font-light text-white/65 leading-relaxed">
                  Permette di lasciare una traccia scritta del proprio Tempo:
                  un pensiero, una decisione, una riflessione o qualcosa che
                  non vuoi semplicemente lasciare passare. Non è necessario
                  raccontare tutta la giornata — devi decidere cosa merita di
                  lasciare un'impronta.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 px-3.5 py-3 rounded-xl bg-white/[0.02] border border-white/10">
              <div className="w-8 h-8 rounded-lg bg-emerald-400/10 border border-emerald-400/20 flex items-center justify-center text-emerald-300 shrink-0">
                <Camera className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <div className="text-[13px] sm:text-sm text-white/90 font-light mb-1">
                  Istanti
                </div>
                <p className="text-[12px] sm:text-[13px] font-light text-white/65 leading-relaxed">
                  Permette di catturare attraverso la fotocamera due momenti
                  della giornata:{" "}
                  <span className="text-white/85">1 Istante entro le 12:00</span>{" "}
                  e{" "}
                  <span className="text-white/85">1 Istante entro le 24:00</span>.
                  Non puoi riempire una galleria di fotografie: devi scegliere.
                  Il limite serve proprio a questo — portarti a riconoscere
                  quali momenti meritano davvero di essere conservati.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 5. Modalità TIME Watch — sette indicatori */}
        <section
          data-testid="tw-guide-section-indicators"
          className="mb-7"
        >
          <SectionTitle
            eyebrow="5 · Modalità TIME Watch"
            title="I sette indicatori"
          />
          <p className="text-[13px] sm:text-sm font-light text-white/60 leading-relaxed mb-4">
            È la modalità centrale. È composta da sette indicatori.
          </p>
          <ol className="space-y-2.5" data-testid="tw-guide-indicators-list">
            {INDICATORS.map((it) => {
              const Ico = it.icon;
              return (
                <li
                  key={it.n}
                  data-testid={`tw-guide-indicator-${it.n}`}
                  className="flex items-start gap-3 px-3.5 py-3 rounded-xl bg-white/[0.02] border border-white/10"
                >
                  <div className="w-8 h-8 rounded-lg bg-emerald-400/10 border border-emerald-400/20 flex items-center justify-center text-emerald-300 shrink-0 relative">
                    <Ico className="w-4 h-4" />
                    <span className="absolute -top-1.5 -left-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-black border border-emerald-400/50 text-[9px] tracking-[0.05em] text-emerald-300 flex items-center justify-center font-light">
                      {it.n}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[13px] sm:text-sm text-white/90 font-light mb-1 leading-snug">
                      {it.title}
                    </div>
                    <p className="text-[12px] sm:text-[13px] font-light text-white/65 leading-relaxed">
                      {it.text}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        </section>

        {/* 6. Ora TIME */}
        <section
          data-testid="tw-guide-section-ora-time"
          className="mb-7"
        >
          <SectionTitle
            eyebrow="6 · Ora TIME"
            title="L'assistente vocale del TIME Watch"
          />
          <div className="space-y-3 text-[13px] sm:text-sm font-light text-white/70 leading-relaxed">
            <p>
              Per facilitare l'utilizzo del TIME Watch è stato sviluppato anche
              il suo assistente vocale:{" "}
              <span className="text-emerald-300/90">ORA TIME</span>.
            </p>
            <p>
              Così come esistono "Ehi Google", Siri e Alexa, TIME dispone del
              proprio sistema di interazione vocale.
            </p>
            <div className="flex items-start gap-3 px-3.5 py-3 rounded-xl bg-emerald-400/[0.05] border border-emerald-400/20">
              <Mic className="w-4 h-4 text-emerald-300 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-300/80 mb-1">
                  Esempio
                </div>
                <div className="text-[13px] sm:text-sm text-emerald-100/90 font-light">
                  &ldquo;Ora TIME, modalità TIME Watch.&rdquo;
                </div>
              </div>
            </div>
            <p>
              L'obiettivo è consentire all'utente di richiamare modalità e
              funzioni senza dover navigare manualmente nell'interfaccia.
              Questa caratteristica diventerà ancora più importante con il
              futuro TIME Watch da polso, permettendo di interagire con il
              dispositivo direttamente attraverso la voce.
            </p>
          </div>
        </section>

        {/* 7. Come iniziare */}
        <section
          data-testid="tw-guide-section-start"
          className="mb-7"
        >
          <SectionTitle eyebrow="7 · Come iniziare" title="Passo dopo passo" />
          <p className="text-[13px] sm:text-sm font-light text-white/60 leading-relaxed mb-3">
            Non cercare di comprendere tutto contemporaneamente.
          </p>
          <ol className="space-y-1.5 list-none">
            {START.map((step, i) => (
              <li
                key={i}
                className="flex items-start gap-3 text-[13px] sm:text-sm font-light text-white/75 leading-relaxed"
              >
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-emerald-400/70 shrink-0" />
                {step}
              </li>
            ))}
          </ol>
          <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-4">
            <p className="text-[12px] sm:text-[13px] font-light text-white/65 leading-relaxed">
              E soprattutto torna a guardare questi valori nel corso dei giorni.
              Perché TIME Watch non nasce per fornirti un'altra serie di numeri
              da controllare.
            </p>
            <p className="mt-2 text-[13px] sm:text-sm text-white/90 font-light leading-relaxed">
              Nasce per farti vedere qualcosa che hai sempre avuto davanti, ma
              che probabilmente non hai mai osservato in questo modo:{" "}
              <span className="text-emerald-300/90">
                il tuo rapporto con il Tempo.
              </span>
            </p>
          </div>
        </section>

        {/* Actions */}
        <div className="flex items-center justify-between gap-3 pt-2 border-t border-white/8">
          <button
            type="button"
            onClick={onBack || onClose}
            data-testid="time-watch-guide-back"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-[11px] tracking-[0.22em] uppercase text-white/60 hover:text-white hover:border-white/30 transition"
          >
            <Compass className="w-3.5 h-3.5" />
            Torna al Centro Guida
          </button>
          <button
            type="button"
            onClick={onClose}
            data-testid="time-watch-guide-ok"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-emerald-500/90 text-black text-sm tracking-wide font-medium hover:bg-emerald-400 transition"
          >
            HO CAPITO
          </button>
        </div>
      </div>
    </div>
  );
}
